import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { Entitlements, MediaType, Profile, ResourceType } from '#/client';
import devtoolsApi from '#/dev/devtools';
import { useConfig } from '#/hooks';
import { mergeAnalyticsFeatureWithIntegrations } from '#/plugins/analytics/analyticsUtils';
import pluginManager from '#/plugins/pluginManager';
import { ThemeConfig } from '#/service/config';
import { AppStatus } from '#/service/constants';
import { initialiseDefaultSettings } from '#/service/user/settings';
import { Dictionary, ignoreError } from '#/service/utils';
import { initAuth } from '#/store/auth';

import {
  defaultGlobalContext,
  GlobalContextProvider,
} from './useGlobalContext';

interface Boot {
  isBooted: boolean;
  isActive: boolean;
  isDictionaryReady: boolean;
  isPluginManagerReady: boolean;
}

export const mediaTypeToResourceType: Dictionary<MediaType, ResourceType> = {
  [MediaType.Item]: ResourceType.Item,
  [MediaType.Collection]: ResourceType.Collection,
};

export interface UserState {
  userId?: string;
  sessionId?: string;
  entitlements?: Entitlements;
  profile?: Profile;
}

export interface GlobalContextInterface {
  // bootstrap info
  boot: Boot;
  // theme data extracted from the config
  theme: ThemeConfig;
}

interface GlobalStateProps {
  children?: ReactNode;
}

const GlobalState = ({ children }: GlobalStateProps) => {
  const config = useConfig();
  const { app, restrictions, theme, more, features } = config;
  const [boot, setBoot] = useState<Boot>(defaultGlobalContext.boot);

  // TODO: This is basically our bootstrap process. The plan is to eventually move out steps from here individually,
  //  to more appropriate places, ex: initAuth -> AuthProvider. We decided to postpone that after the context split is done.
  useEffect(() => {
    const isActive = app.status === AppStatus.Active;
    const isGeoBlocked = restrictions.geoblocked;

    // we only need these steps if we're not geoblocked
    if (!isGeoBlocked) {
      // initialise default settings
      initialiseDefaultSettings(more.settings);
      // start loading plugins
      pluginManager.loadFeaturePlugins({
        ...features,
        // merge S3 feature configs and Core integration configs for analytics
        analytics: mergeAnalyticsFeatureWithIntegrations(config),
      });
      pluginManager
        .allPluginsLoadedPromise()
        .then(() => {
          setBoot((prevBoot) => ({
            ...prevBoot,
            isPluginManagerReady: true,
          }));
        })
        .catch(ignoreError);
    }

    !isGeoBlocked && initAuth();

    setBoot((prevBoot) => ({
      ...prevBoot,
      isActive: isActive,
      isBooted: true,
      isDictionaryReady: true,
    }));
  }, [app.status, config, features, more.settings, restrictions.geoblocked]);

  const context = useMemo(
    () => ({
      ...defaultGlobalContext,
      boot,
    }),
    [boot]
  );

  // set current globalContext in devtools.
  if (process.env.goal === 'local') {
    devtoolsApi.setContexts({ global: context });
  }

  return (
    <GlobalContextProvider config={context}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </GlobalContextProvider>
  );
};

export default GlobalState;
